/** TanStack Query hooks for AI-generated personal lessons: list, generate, delete. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { PersonalLesson, PaginatedResponse } from '@formency/shared';
import type {
  PersonalLessonListParams,
  GeneratePersonalLessonVars,
  GeneratePersonalLessonResult,
} from '@/shared/types';
import { jobKeys } from './useJobs';

// ─── Query key factory ─────────────────────────────────────────────────────

export const personalLessonKeys = {
  all: ['f', 'personal-lessons'] as const,
  list: (params?: PersonalLessonListParams) => [...personalLessonKeys.all, 'list', params] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch paginated list of the current user's personal lessons. */
export function usePersonalLessons(params?: PersonalLessonListParams) {
  return useQuery<PaginatedResponse<PersonalLesson>>({
    queryKey: personalLessonKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<PersonalLesson>>(
        '/me/personal-lessons',
        params as Record<string, unknown> | undefined
      ),
    enabled: params !== undefined,
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Generate a new personal lesson. Returns a background job reference. */
export function useGeneratePersonalLesson() {
  const queryClient = useQueryClient();

  return useMutation<GeneratePersonalLessonResult, Error, GeneratePersonalLessonVars>({
    mutationFn: ({ prompt, file }) => {
      const formData = new FormData();
      formData.append('prompt', prompt);
      if (file) formData.append('file', file);

      // Don't set Content-Type — axios auto-detects FormData and adds the
      // correct multipart/form-data header with boundary parameter.
      return api.post<GeneratePersonalLessonResult>('/me/personal-lessons/generate', formData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: personalLessonKeys.all });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/** Delete a personal lesson by its ID. */
export function useDeletePersonalLesson() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (lessonId) => api.del(`/me/personal-lessons/${lessonId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: personalLessonKeys.all });
    },
  });
}
